import { useEffect, useMemo, useState } from 'react';
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import cn from './cn.js';
import { inputBase } from './Input.js';

const regionNames = typeof Intl !== 'undefined' && Intl.DisplayNames ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;

export function countryName(code) {
  try {
    return regionNames ? regionNames.of(code) : code;
  } catch (error) {
    return code;
  }
}

export function flagEmoji(code = '') {
  return code
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

const COUNTRIES = getCountries()
  .map((code) => ({ code, name: countryName(code), dial: getCountryCallingCode(code) }))
  .sort((a, b) => a.name.localeCompare(b.name));

const PRIORITY = ['RO', 'US', 'GB', 'DE', 'FR', 'ES', 'IT', 'NL'];

/**
 * Parses any stored value (E.164 like "+40711111111", or a legacy national
 * string like "0711 111 111") into { country, national, e164, valid }.
 */
export function parsePhone(value, defaultCountry = 'RO') {
  if (!value) return { country: defaultCountry, national: '', e164: '', valid: false };
  const parsed = parsePhoneNumberFromString(String(value), defaultCountry);
  if (!parsed) return { country: defaultCountry, national: String(value), e164: '', valid: false };
  return {
    country: parsed.country || defaultCountry,
    national: parsed.formatNational(),
    e164: parsed.number,
    valid: parsed.isValid(),
  };
}

/**
 * PhoneInput: international phone number with a country selector.
 * `value` is the E.164 string (or legacy national string); `onChange(e164, meta)`.
 */
export default function PhoneInput({ id, value, onChange, defaultCountry = 'RO', disabled = false, invalid = false, className, placeholder, size = 'md' }) {
  const initial = useMemo(() => parsePhone(value, defaultCountry), [value, defaultCountry]);
  const [country, setCountry] = useState(initial.country);
  const [national, setNational] = useState(initial.national);

  // Keep local state in sync when the stored value changes from outside.
  useEffect(() => {
    setCountry(initial.country);
    setNational(initial.national);
  }, [initial.country, initial.national]);

  const emit = (nextCountry, nextNational) => {
    const parsed = parsePhoneNumberFromString(nextNational || '', nextCountry);
    const e164 = parsed ? parsed.number : '';
    const valid = parsed ? parsed.isValid() : false;
    onChange?.(nextNational.trim() ? e164 || nextNational : '', { country: nextCountry, valid, national: nextNational });
  };

  const handleNumber = (e) => {
    const raw = e.target.value;
    // Format as the user types (national format for the selected country).
    const formatted = raw.trim() ? new AsYouType(country).input(raw) : '';
    setNational(formatted);
    emit(country, formatted);
  };

  const handleCountry = (e) => {
    const next = e.target.value;
    setCountry(next);
    emit(next, national);
  };

  const ordered = [...PRIORITY.map((code) => COUNTRIES.find((c) => c.code === code)).filter(Boolean), ...COUNTRIES.filter((c) => !PRIORITY.includes(c.code))];
  const heights = { sm: 'h-8', md: 'h-9', lg: 'h-10' };

  return (
    <div className={cn('flex', className)}>
      <div className="relative shrink-0">
        <select
          aria-label="Country"
          value={country}
          onChange={handleCountry}
          disabled={disabled}
          className={cn(inputBase, heights[size] || heights.md, 'w-[7.5rem] appearance-none rounded-r-none bg-none pl-3 pr-7 font-medium')}
        >
          {ordered.map((c, index) => (
            <option key={`${c.code}-${index}`} value={c.code}>
              {flagEmoji(c.code)} {c.code} +{c.dial}
            </option>
          ))}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
      </div>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={national}
        onChange={handleNumber}
        disabled={disabled}
        placeholder={placeholder || 'Phone number'}
        aria-invalid={invalid || undefined}
        className={cn(inputBase, heights[size] || heights.md, '-ml-px rounded-l-none px-3 focus:z-10', invalid && 'ring-red-300 focus:ring-red-500')}
      />
    </div>
  );
}
